import { type FC, type ReactNode, useEffect, useRef } from "react";
import { useSettingsStore } from "@/stores/useSettingsStore.ts";
import NothingAudio from "@/assets/audio/250msOfNothing.mp3";
import { toast } from "sonner";

interface SettingProps {
  children?: ReactNode;
}

const audio = new Audio(NothingAudio);

export const SettingsWrapper: FC<SettingProps> = ({ children }) => {
  const { fetchSettings, settings } = useSettingsStore();

  const hasAudio = useRef<boolean>(false);
  const toastRef = useRef<number | string>(null);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const fontOffset = 0.05 * settings.FontSizeOffset;
    document.documentElement.style.cssText = `
      --13px: ${0.75 + fontOffset}rem;
      --14px: ${0.875 + fontOffset}rem;
      --15px: ${0.9375 + fontOffset}rem;
      --16px: ${1 + fontOffset}rem;
      --17px: ${1.0625 + fontOffset}rem;
      --18px: ${1.125 + fontOffset}rem;
      --19px: ${1.1875 + fontOffset}rem;
      --20px: ${1.25 + fontOffset}rem;
      --21px: ${1.3125 + fontOffset}rem;
      --katexSizing: ${1.21 + fontOffset}rem;
    `;
  }, [settings.FontSizeOffset]);

  useEffect(() => {
    if (settings.MiscDisableTooltips) {
      document.body.style.setProperty("--tooltip-opacity", "0");
    } else {
      document.body.style.removeProperty("--tooltip-opacity");
    }
  }, [settings.MiscDisableTooltips]);

  useEffect(() => {
    if (settings.SmallHeight) {
      document.body.style.setProperty("--small-height", "1");
    } else {
      document.body.style.removeProperty("--small-height");
    }
  }, [settings.SmallHeight]);

  useEffect(() => {
    const audioCheck = () => {
      audio
        .play()
        .then(() => {
          if (toastRef.current) {
            toast.dismiss(toastRef.current);
            toastRef.current = null;
          }

          audio.pause();
          audio.remove();

          hasAudio.current = true;
        })
        .catch(() => {
          hasAudio.current = false;

          if (toastRef.current) return;

          toastRef.current = toast.warning("Audio is currently disabled!", {
            position: "bottom-center",
            duration: Infinity,
            description: "Interact with this page or close this notification to enable audio",
          });
        });

      setTimeout(() => {
        if (!hasAudio.current) audioCheck();
      }, 250);
    };

    audioCheck();

    return () => {
      if (toastRef.current) {
        toast.dismiss(toastRef.current);
        toastRef.current = null;
      }

      if (audio) {
        audio.pause();
        audio.remove();
      }
    };
  });

  return <>{children}</>;
};
